import tippy from "tippy.js";
import Accordions from "a11y-accordion-component";
import Sortable from "sortablejs";
import { debounce } from "../utils/debounce";
import { randomId } from "../utils/randomId";
import { breakpoints } from "../../variables.json";

const sortableEl = document.querySelector(".js-sticky");
const mediaMediumMatch = matchMedia(`(min-width:${breakpoints.medium})`)
	.matches;

if (sortableEl && mediaMediumMatch) {
	new Sortable(sortableEl, {
		swapThreshold: 0.5,
		animation: 150,
		onChange: function(evt) {
			sortableMoveTerms(evt);
		},
		onEnd: function(evt) {
			sortableMoveTerms(evt);
		}
	});
}

function sortableMoveTerms(evt) {
	var index = evt.item.dataset.index;
	var all = document.querySelectorAll(
		".diff-table__row-term[data-index='" + index + "']"
	);
	[...all].forEach(el => {
		var newel = el.parentNode.querySelectorAll(".diff-table__row-term")[
			evt.newIndex
		];
		if (evt.oldIndex < evt.newIndex) {
			el.before(newel);
		} else {
			el.after(newel);
		}
	});
}

export const diffTableTippyConfig = {
	theme: "blank",
	interactive: true,
	animateFill: false,
	animation: "fade",
	duration: 0,
	trigger: "click",
	allowHTML: true,
	arrow: false,
	placement: "top",
	content: getTemplateString,
	onTrigger(instance, event) {
		instance.setContent(getTemplateString(instance.reference));
	},
	onMount(instance) {
		const id = "diff-select";
		const accordion = instance.popper.querySelector(`#${id}`);
		const triggers = accordion.querySelectorAll(
			"[id^='select_accordion-simple-trigger-']"
		);

		const current = instance.reference.dataset.current;

		instance.popper
			.querySelector(".dropdown-select")
			.setAttribute("data-current", current);

		[...triggers].forEach(el => {
			el.id = randomId("select_accordion-simple-trigger-");
			el.dataset.controls = el.nextElementSibling.id = randomId(
				"select_accordion-simple-panel-"
			);
		});

		Accordions.render(id, {
			isCollapsible: false,
			isMultiSelectable: false
		});

		if (!instance._cb) {
			instance._cb = diffSelectClickHandler.bind(instance.reference);
			accordion.addEventListener("click", instance._cb, false);
		}
	},
	onHide(instance) {
		const id = "diff-select";
		const accordion = instance.popper.querySelector(`#${id}`);
		accordion.removeEventListener("click", instance._cb, false);
		instance._cb = null;
		Accordions.destroy(id);
	}
};

class DiffTable {
	constructor(selector) {
		this.widget = document.querySelector(selector);
		if (this.widget) {
			this.prev = this.widget.querySelector(".js-prev");
			this.next = this.widget.querySelector(".js-next");
			this.translatedElements = [...this.widget.querySelectorAll(".js-sticky")];
			this.scroll = 0;
			this.limit = mediaMediumMatch ? 3 : 2;

			this.prev.disabled = true;
			this.next.disabled = this.getRemoveButtonsCount() <= this.limit;

			this.bindEventListeners();
		}
	}

	/** @private */
	bindEventListeners() {
		this.slidePrev = this.slidePrev.bind(this);
		this.slideNext = this.slideNext.bind(this);
		this.removeProduct = this.removeProduct.bind(this);

		this.prev.addEventListener("click", this.slidePrev, false);
		this.next.addEventListener("click", this.slideNext, false);
		this.widget.addEventListener("click", this.removeProduct, false);

		this._resizeHandler = debounce(() => this.translate(0));
		window.addEventListener("resize", this._resizeHandler, false);
	}

	/** @private */
	unbindEventListeners() {
		this.prev.removeEventListener("click", this.slidePrev, false);
		this.next.removeEventListener("click", this.slideNext, false);
		this.widget.removeEventListener("click", this.removeProduct, false);

		window.removeEventListener("resize", this._resizeHandler, false);
		this._resizeHandler = null;
	}

	/** @private */
	removeProduct(e) {
		const trigger = e.target.closest(".js-remove");

		if (trigger) {
			const index = trigger.dataset.id;
			const elements = [
				...this.widget.querySelectorAll(`[data-index="${index}"]`)
			];
			elements.forEach(el => el.parentElement.removeChild(el));
			this.prev.click();

			if (this.getRemoveButtonsCount() <= this.limit) {
				this.prev.disabled = true;
				this.next.disabled = true;
			}
		}
	}

	/** @public */
	getRemoveButtonsCount() {
		return this.widget.querySelectorAll(".js-removefromcompare").length;
	}

	/** @public */
	getTranslateValueLimit() {
		const elements = [...this.translatedElements[0].children];
		const lastElement = elements[0];
		const width = this.limit * lastElement.clientWidth;
		const totalWidth = elements.reduce(
			(value, el) => value + el.clientWidth,
			0
		);
		return totalWidth - width - this.limit;
	}

	/** @public */
	slidePrev() {
		if (!this.translatedElements[0].children[0]) {
			return false;
		}
		const length = this.translatedElements[0].children[0].clientWidth;
		let scroll = Math.min(this.scroll + length, 0);
		this.translate(scroll);
	}

	/** @public */
	slideNext() {
		const length = this.translatedElements[0].children[0].clientWidth;
		let scroll = this.scroll - length;
		scroll = Math.min(Math.abs(scroll), this.getTranslateValueLimit()) * -1;
		this.translate(scroll);
	}

	/** @public */
	slideEnd() {
		const length = this.translatedElements[0].children[0].clientWidth;
		let scroll = this.scroll - length;
		scroll = Math.min(this.getTranslateValueLimit() * -1, 0);
		this.translate(scroll);
	}

	/** @public */
	translate(value) {
		this.scroll = value;
		this.translatedElements.forEach(el => {
			el.style.transform = `translate(${value}px)`;
		});

		this.prev.disabled = value === 0;
		this.next.disabled = Math.abs(value) >= this.getTranslateValueLimit();
	}

	/**
	 * @public
	 * @param rewind {boolean} Reset slider to initial position
	 */
	recalculate(rewind) {
		this.unbindEventListeners();
		if (rewind) this.translate(0);
		this.constructor(".js-diff-table");
	}
}

var diff = new DiffTable(".js-diff-table");
tippy(".js-diff-select-trigger", diffTableTippyConfig);

function diffSelectClickHandler(e) {
	const target = e.target.closest(".comparison-select__item");
	if (target) {
		const index = this.closest("[data-index]").dataset.index;
		console.log(index, target);
		// TODO: zmena vybraneho produktu
	}
}

function getTemplateString(reference) {
	return reference
		.closest(".js-diff-table")
		.querySelector(".js-diff-select-list").innerHTML;
}

export const diffTable = diff;
